import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tag, Loader2 } from "lucide-react";

export function CategoryManager({ categories, onCreate, onUpdate, onDelete, labelField = "label", valueField = "value", nameField = null, slugField = null }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isBlogStyle = !!nameField;

  const openCreate = () => {
    setEditing(null);
    setForm(isBlogStyle ? { [nameField]: "", [slugField]: "", description: "" } : { [labelField]: "", [valueField]: "" });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm(isBlogStyle ? { [nameField]: cat[nameField] || "", [slugField]: cat[slugField] || "", description: cat.description || "" } : { [labelField]: cat[labelField] || "", [valueField]: cat[valueField] || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (isBlogStyle && !form[nameField]) return toast.error("Name is required");
    if (!isBlogStyle && !form[labelField]) return toast.error("Label is required");
    setSaving(true);
    try {
      if (editing) {
        await onUpdate(editing.id, form);
        toast.success("Category updated");
      } else {
        if (!isBlogStyle && !form[valueField]) {
          form[valueField] = form[labelField].toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
        }
        if (isBlogStyle && !form[slugField]) {
          form[slugField] = form[nameField].toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
        }
        await onCreate(form);
        toast.success("Category created");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save category");
    } finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${isBlogStyle ? cat[nameField] : cat[labelField]}"? This cannot be undone.`)) return;
    try {
      await onDelete(cat.id);
      toast.success("Category deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="category-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-navy text-sm">Categories ({categories.length})</h3>
        <Button size="sm" variant="outline" onClick={openCreate} data-testid="add-category-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Category
        </Button>
      </div>
      <div className="space-y-2">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`cat-row-${cat.id}`}>
            <div className="flex items-center gap-2">
              <Tag className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-sm text-navy font-medium">{isBlogStyle ? cat[nameField] : cat[labelField]}</span>
              <span className="text-xs text-slate-400">({isBlogStyle ? cat[slugField] : cat[valueField]})</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                <Pencil className="w-3.5 h-3.5 text-slate-500" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(cat)} data-testid={`delete-cat-${cat.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </Button>
            </div>
          </div>
        ))}
        {categories.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No categories yet</p>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">{editing ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {isBlogStyle ? (
              <>
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <Input value={form[nameField] || ""} onChange={(e) => setForm({ ...form, [nameField]: e.target.value })} data-testid="cat-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Slug</label>
                  <Input value={form[slugField] || ""} onChange={(e) => setForm({ ...form, [slugField]: e.target.value })} placeholder="auto-generated" data-testid="cat-slug-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc-input" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium">Label *</label>
                  <Input value={form[labelField] || ""} onChange={(e) => setForm({ ...form, [labelField]: e.target.value })} data-testid="cat-label-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Value (slug)</label>
                  <Input value={form[valueField] || ""} onChange={(e) => setForm({ ...form, [valueField]: e.target.value })} placeholder="auto-generated" data-testid="cat-value-input" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-category-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
